/**
 * API Docs — Swagger UI playground
 * Feb 2026
 *
 * Loads swagger-ui-dist via CDN and points it at our curated OpenAPI
 * spec (/api/v1/developer/openapi.json). Users prompt-paste a PAT once
 * and then hit "Try it" on any endpoint.
 */
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Code2, ExternalLink, KeyRound } from "lucide-react";
import PageHeader from "@/components/PageHeader";

const SubNav = ({ active }) => (
  <div className="flex items-center gap-4 border-b border-border pb-1" data-testid="developer-subnav">
    <a href="/app/developer" className={`px-3 py-1.5 text-sm ${active==='tokens'?'font-medium border-b-2 border-primary text-foreground':'text-muted-foreground hover:text-foreground'}`}>Tokens</a>
    <a href="/app/developer/webhooks" className={`px-3 py-1.5 text-sm ${active==='webhooks'?'font-medium border-b-2 border-primary text-foreground':'text-muted-foreground hover:text-foreground'}`}>Webhooks</a>
    <a href="/app/developer/docs" className={`px-3 py-1.5 text-sm ${active==='docs'?'font-medium border-b-2 border-primary text-foreground':'text-muted-foreground hover:text-foreground'}`}>API Docs</a>
  </div>
);

const AXIOS_BASE = (api.defaults?.baseURL || "").replace(/\/$/, "");
// Env-aware — prefer browser origin so preview / prod point at whichever
// host the user is actually viewing.
const DOCS_BASE = (() => {
  try { return `${window.location.origin}/api/v1`; }
  catch { return AXIOS_BASE; }
})();

// Emergent-agent forbids inline <script>-load without CSP tolerance so
// we pull the two library scripts + their stylesheet ourselves.
const SWAGGER_CSS = "https://unpkg.com/swagger-ui-dist@5.17.14/swagger-ui.css";
const SWAGGER_JS  = "https://unpkg.com/swagger-ui-dist@5.17.14/swagger-ui-bundle.js";

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
    const s = document.createElement("script");
    s.src = src; s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("failed to load " + src));
    document.head.appendChild(s);
  });
}
function loadCss(href) {
  if (document.querySelector(`link[href="${href}"]`)) return;
  const l = document.createElement("link");
  l.rel = "stylesheet"; l.href = href;
  document.head.appendChild(l);
}

export default function DeveloperApiDocs() {
  const containerRef = useRef(null);
  const [tab, setTab] = useState("playground");
  const [guide, setGuide] = useState(null);

  useEffect(() => {
    if (tab !== "guide" || guide) return;
    api.get("/developer/getting-started")
      .then((r) => setGuide(r.data?.markdown || ""))
      .catch(() => setGuide("# Failed to load guide\nCheck your network."));
  }, [tab, guide]);

  useEffect(() => {
    if (tab !== "playground") return;
    let cancelled = false;
    loadCss(SWAGGER_CSS);
    (async () => {
      try {
        await loadScript(SWAGGER_JS);
        if (cancelled) return;
        // Fetch the spec through axios so we hit the same base URL and
        // carry the JWT (Swagger's own request is unauthenticated and
        // would 401 otherwise).
        const { data: spec } = await api.get("/developer/openapi.json");
        if (cancelled) return;
        // eslint-disable-next-line no-undef
        const ui = window.SwaggerUIBundle({
          spec,
          domNode: containerRef.current,
          layout: "BaseLayout",
          deepLinking: true,
          docExpansion: "list",
          filter: true,
          persistAuthorization: true,
        });
        // Feb 2026 · Auto-prefill the Authorize dialog with the caller's
        // current JWT so Try-It works one-click. PATs also accepted —
        // the same header format. Users can override with a PAT via the
        // Authorize modal for scope-testing.
        try {
          const jwt = localStorage.getItem("token")
            || (JSON.parse(localStorage.getItem("acquilix.session") || "{}").token);
          if (jwt) {
            ui.preauthorizeApiKey("PAT", jwt);
          }
        } catch { /* no-op */ }
      } catch {
        if (containerRef.current) {
          containerRef.current.innerHTML =
            '<div style="padding:24px;font-size:13px;color:#a94442;border:1px solid #f0c1c1;background:#fef4f4">Failed to load Swagger UI. Check your network and try again.</div>';
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="pb-8">
      <PageHeader icon={KeyRound} title="Developer" subtitle="OpenAPI spec + Try-It playground." />
      <div className="px-4 sm:px-6 md:px-10 space-y-4" data-testid="developer-api-docs-page">
        <SubNav active="docs" />
      <div className="border border-primary/40 bg-primary/5 p-5 flex items-start gap-4">
        <Code2 className="w-6 h-6 text-primary shrink-0 mt-0.5" />
        <div className="space-y-1 flex-1">
          <div className="label-eyebrow">Acquilix Public API · v1</div>
          <p className="text-sm text-muted-foreground">
            Every endpoint below accepts a <span className="font-mono">Personal Access Token</span> as
            <span className="font-mono ml-1">Authorization: Bearer acq_pat_...</span>.
            Click <b>Authorize</b> in the panel then <b>Try it</b> on any operation.
            Raw spec:{" "}
            <a href={`${DOCS_BASE}/developer/openapi.json`} target="_blank" rel="noreferrer"
                className="underline text-primary inline-flex items-center gap-1">
              openapi.json <ExternalLink className="w-3 h-3" />
            </a>
          </p>
        </div>
      </div>
      {/* Feb 2026 · Playground / Getting-started tab toggle. */}
      <div className="flex items-center gap-1 border-b border-border" data-testid="api-docs-tabs">
        <button onClick={() => setTab("playground")}
                 className={`px-4 py-2 text-sm ${tab === "playground" ? "font-semibold border-b-2 border-primary text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                 data-testid="api-docs-tab-playground">Try-It playground</button>
        <button onClick={() => setTab("guide")}
                 className={`px-4 py-2 text-sm ${tab === "guide" ? "font-semibold border-b-2 border-primary text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                 data-testid="api-docs-tab-guide">Getting started guide</button>
      </div>
      {tab === "playground" && (
        <div ref={containerRef} className="bg-background border border-border"
              data-testid="swagger-ui-mount" />
      )}
      {tab === "guide" && (
        <div className="border border-border p-6 bg-background" data-testid="api-docs-guide">
          {guide === null ? (
            <div className="text-sm text-muted-foreground">Loading guide…</div>
          ) : (
            <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">{guide}</pre>
          )}
        </div>
      )}
      </div>
    </div>
  );
}
